import { useEffect, useRef, useState } from 'react'

import { faTruckFast } from '@fortawesome/pro-solid-svg-icons'
import { styled, Typography } from '@mui/material'

import { Icon } from '../Icon/Icon'

export const BackdropLoading = ({ open = false }) => {
  const [position, setPosition] = useState(0)
  const animationRef = useRef<number>()
  const previousTimeRef = useRef<number>()
  const patternWidth = 70 // Width of one complete pattern (dash + gap)

  const animate = (currentTime: number) => {
    if (!previousTimeRef.current) {
      previousTimeRef.current = currentTime
    }

    const deltaTime = currentTime - previousTimeRef.current

    previousTimeRef.current = currentTime

    // Update position
    setPosition(prevPosition => {
      const newPosition = prevPosition - (deltaTime * 0.1)

      // Reset position smoothly when it gets too far
      return newPosition <= -patternWidth ? newPosition + patternWidth : newPosition
    })

    animationRef.current = requestAnimationFrame(animate)
  }

  useEffect(() => {
    if (open) {
      animationRef.current = requestAnimationFrame(animate)
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  if (!open) return null

  return (
    <LoadingOverlay>
      <AnimationContainer>
        <RoadContainer>
          <RoadStrip position="top">
            <RoadPattern $position={position} />
          </RoadStrip>
          <RoadStrip position="bottom">
            <RoadPattern $position={position} />
          </RoadStrip>
        </RoadContainer>

        <TruckContainer>
          <Icon
            icon={faTruckFast}
            sx={{
              fontSize: 96,
              color: 'white',
            }}
          />
        </TruckContainer>
      </AnimationContainer>

      <LoadingText>
        <Typography variant="h6" sx={{ color: 'white' }}>
          Loading...
        </Typography>
      </LoadingText>
    </LoadingOverlay>
  )
}

const LoadingOverlay = styled('div')`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1400;
`

const AnimationContainer = styled('div')`
  position: relative;
  height: 120px;
  width: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`

const RoadContainer = styled('div')`
  position: absolute;
  inset: 0;
  mask-image: linear-gradient(
    to right,
    transparent,
    black 10%,
    black 90%,
    transparent
  );
`

const RoadStrip = styled('div')<{ position: 'top' | 'bottom' }>`
  position: absolute;
  ${({ position }) => position === 'top' ? 'top: 20px;' : 'bottom: 20px;'}
  width: 100%;
  height: 4px;
`

const RoadPattern = styled('div')<{ $position: number }>`
  position: absolute;
  width: 400%;
  height: 100%;
  left: 0;
  background-image: repeating-linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.9) 0px,
    rgba(255, 255, 255, 0.9) 30px,
    transparent 30px,
    transparent 70px
  );
  transform: translateX(${props => props.$position}px);
  will-change: transform;
`

const TruckContainer = styled('div')`
  position: absolute;
  top: 2px;
  animation: bounce 0.5s ease infinite alternate;
  will-change: transform;

  @keyframes bounce {
    0% {
      transform: translateY(0px);
    }
    100% {
      transform: translateY(-10px);
    }
  }
`

const LoadingText = styled('div')`
  margin-top: ${({ theme }) => theme.spacing(2)};
  color: white;
  font-size: 1.2rem;
  font-weight: 500;
  animation: fade 1.5s ease infinite alternate;
  will-change: opacity;

  @keyframes fade {
    0% {
      opacity: 0.7;
    }
    100% {
      opacity: 1;
    }
  }
`